/**
 * DemocratCharts — main chart dashboard.
 *
 * Orchestrates the multi-chart layout:
 *   - Layout (header) — global controls including the chart-count selector
 *   - DrawingToolbar (left) — applies to the active pane
 *   - Grid of ChartPanes (center) — 1/2/4/6/8 layout
 *   - ReplayBar + TradeHistory (below grid) — only in count=1 mode
 *   - RightSidebar (right) — Watchlist + Trading, context-routes to active pane
 *
 * Hotkeys (drawing, trading, replay) are subscribed here and dispatched
 * against the active pane's symbol via the dashboard store.
 */

import { useEffect, useCallback, useRef } from "react";
import { Scissors, X, Info } from "lucide-react";
import clsx from "clsx";
import { Layout } from "@/components/Layout";
import { ChartPane } from "@/components/chart/ChartPane";
import { ReplayBar } from "@/components/chart/ReplayBar";
import { DrawingToolbar } from "@/components/chart/DrawingToolbar";
import { IndicatorsButton } from "@/components/chart/IndicatorsButton";
import { ChartCountSelector } from "@/components/chart/ChartCountSelector";
import { TradeHistory } from "@/components/trading/TradeHistory";
import { RightSidebar } from "@/components/RightSidebar";
import { useReplayPlayback } from "@/hooks/useReplayPlayback";
import { useReplayHotkeys } from "@/hooks/useReplayHotkeys";
import { useTradingHotkeys } from "@/hooks/useTradingHotkeys";
import { useDrawingHotkeys } from "@/hooks/useDrawingHotkeys";
import { useCandles } from "@/hooks/useCandles";
import { useTickStream } from "@/hooks/useTickStream";
import { useTrades } from "@/hooks/useTrades";
import { useReplayStore } from "@/store/replay";
import { useTradingStore } from "@/store/trading";
import { useDrawingsStore } from "@/store/drawings";
import { useDashboardStore, type ChartCount } from "@/store/dashboard";
import { TIMEFRAME_DEFAULT_DAYS, type SymbolEntry } from "@/lib/symbols";

const GRID_CLASS_BY_COUNT: Record<ChartCount, string> = {
  1: "grid-cols-1 grid-rows-1",
  2: "grid-cols-2 grid-rows-1",
  4: "grid-cols-2 grid-rows-2",
  6: "grid-cols-3 grid-rows-2",
  8: "grid-cols-4 grid-rows-2",
};

const PROMPT_LABELS_BY_TOOL: Record<string, (n: number) => string> = {
  trend:           (n) => `Trend line · click ${n === 0 ? "first" : "second"} point`,
  horizontal:      ()  => `Horizontal line · click anywhere`,
  fib_retracement: (n) => `Fib retracement · click ${n === 0 ? "swing high" : "swing low"}`,
  fib_extension:   (n) => `Fib extension · click ${["first leg start", "first leg end", "retracement anchor"][n] ?? "next point"}`,
  cycle:           (n) => `Cycle lines · click ${n === 0 ? "anchor" : "second cycle"}`,
  eraser:          ()  => `Eraser · click a drawing`,
};

export function ChartPage() {
  const { count, panes, activeId, setActive, updatePane } = useDashboardStore();
  const active = panes.find((p) => p.id === activeId) ?? panes[0];
  const singleMode = count === 1;

  // ─── Replay (only enabled in single mode) ──────────────────────────────
  const {
    active: replayActive, setupMode, currentIndex,
    setTotalCandles, reset: resetReplay,
    enter: enterReplay, exit: exitReplay, addBookmark,
  } = useReplayStore();
  useReplayPlayback();

  // Force-exit replay if user switches to multi mode while it's active
  useEffect(() => {
    if (!singleMode && replayActive) exitReplay();
  }, [singleMode, replayActive, exitReplay]);

  // ─── Active pane's candle data (drives trading + replay + sidebar) ─────
  const days = TIMEFRAME_DEFAULT_DAYS[active.timeframe] ?? 365;
  const { data: activeData } = useCandles({
    symbol: active.symbol, exchange: active.exchange, timeframe: active.timeframe, days,
  });

  useEffect(() => { resetReplay(); }, [active.symbol, active.exchange, active.timeframe, resetReplay]);
  useEffect(() => { setTotalCandles(activeData?.candles.length ?? 0); }, [activeData?.candles.length, setTotalCandles]);

  // ─── Trading on the active pane ────────────────────────────────────────
  const { accountBalance, open, setOpen, updateOpenSL, updateOpenTP } = useTradingStore();
  const { trades, createTrade, updateTrade, closeTrade, deleteTrade } =
    useTrades({ symbol: active.symbol, exchange: active.exchange, timeframe: active.timeframe });

  const visibleActiveCandles = singleMode && replayActive && !setupMode && currentIndex !== null
    ? activeData?.candles.slice(0, currentIndex + 1) ?? []
    : activeData?.candles ?? [];
  const currentCandle = visibleActiveCandles[visibleActiveCandles.length - 1] ?? null;

  // F3: live ticks reach the trade engine, not just the chart pixels.
  // Replay mode uses the revealed bar's close (historical fidelity); live mode
  // prefers the streaming price and falls back to the last fetched close.
  const { lastTick: activeTick } = useTickStream(active.symbol, active.exchange);
  const replayMode = singleMode && replayActive && !setupMode;
  const currentPrice = replayMode
    ? (currentCandle?.close ?? null)
    : (activeTick?.last_price ?? currentCandle?.close ?? null);

  // ─── Drawings ──────────────────────────────────────────────────────────
  const { activeTool, pendingPoints } = useDrawingsStore();
  useDrawingHotkeys();

  // ─── Buy/Sell hotkeys → buy on the active pane's symbol ────────────────
  const qtyRef = useRef<number>(useTradingStore.getState().defaultQuantity);
  useEffect(() => {
    const unsub = useTradingStore.subscribe((s) => { qtyRef.current = s.defaultQuantity; });
    return unsub;
  }, []);

  const placeOrder = useCallback(
    async (direction: "long" | "short") => {
      if (!currentCandle || !currentPrice || open) return;
      try {
        const created = await createTrade({
          symbol: active.symbol, exchange: active.exchange, timeframe: active.timeframe,
          direction, quantity: qtyRef.current,
          // Live entries are stamped with the tick time, not the stale bar ts.
          entry_ts: replayMode ? currentCandle.ts : (activeTick?.ts ?? currentCandle.ts),
          entry_price: currentPrice,
          account_balance_at_entry: accountBalance,
        });
        setOpen({
          id: created.id,
          symbol: created.symbol, exchange: created.exchange, timeframe: created.timeframe,
          direction: created.direction, quantity: created.quantity,
          entryTs: created.entry_ts, entryPrice: created.entry_price,
          slPrice: created.sl_price, tpPrice: created.tp_price,
        });
      } catch (e) { console.error("Failed to create trade", e); }
    },
    [currentCandle, currentPrice, open, createTrade, active.symbol, active.exchange, active.timeframe, accountBalance, setOpen, replayMode, activeTick]
  );

  // Hotkeys are a one-keystroke, no-confirmation path — restrict them to the
  // deliberate practice context (single-pane replay). Buttons in the trading
  // panel remain available everywhere. NEVER wire live order routing to this.
  useTradingHotkeys({
    onBuy:  () => placeOrder("long"),
    onSell: () => placeOrder("short"),
    enabled: !activeTool && singleMode && replayActive,
  });

  // SL/TP debounced sync
  const slTpDebounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    if (slTpDebounceRef.current) window.clearTimeout(slTpDebounceRef.current);
    slTpDebounceRef.current = window.setTimeout(() => {
      updateTrade(open.id, { sl_price: open.slPrice, tp_price: open.tpPrice }).catch(() => {});
    }, 500);
    return () => { if (slTpDebounceRef.current) window.clearTimeout(slTpDebounceRef.current); };
  }, [open?.slPrice, open?.tpPrice, open?.id, updateTrade]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close on SL/TP during replay
  useEffect(() => {
    if (!open || !replayActive || !currentCandle || !singleMode) return;
    if (currentCandle.ts === open.entryTs) return;
    const { slPrice, tpPrice, direction } = open;
    // Conservative fill model:
    //   1. SL is evaluated BEFORE TP — when one bar's range spans both levels,
    //      the intrabar touch order is unknowable; assume the worst so replay
    //      stats never overstate performance.
    //   2. Gap-through fills at the bar's OPEN, not at the level — a stop
    //      gapped through overnight fills where the market opens.
    let hit: { price: number; result: "win" | "loss" } | null = null;
    const barOpen = currentCandle.open;
    if (direction === "long") {
      if (slPrice !== null && currentCandle.low <= slPrice) {
        hit = { price: barOpen <= slPrice ? barOpen : slPrice, result: "loss" };
      } else if (tpPrice !== null && currentCandle.high >= tpPrice) {
        hit = { price: barOpen >= tpPrice ? barOpen : tpPrice, result: "win" };
      }
    } else {
      if (slPrice !== null && currentCandle.high >= slPrice) {
        hit = { price: barOpen >= slPrice ? barOpen : slPrice, result: "loss" };
      } else if (tpPrice !== null && currentCandle.low <= tpPrice) {
        hit = { price: barOpen <= tpPrice ? barOpen : tpPrice, result: "win" };
      }
    }
    if (hit) {
      useReplayStore.getState().pause();
      closeTrade(open.id, { exit_ts: currentCandle.ts, exit_price: hit.price, result: hit.result })
        .then(() => setOpen(null))
        .catch((e) => console.error("close failed", e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCandle?.ts]);

  // Replay hotkeys + bookmark callback
  useReplayHotkeys({
    onBookmark: () => {
      if (!singleMode) return;
      if (currentIndex === null || !activeData?.candles[currentIndex]) return;
      addBookmark({
        symbol: active.symbol, exchange: active.exchange, timeframe: active.timeframe,
        candleTs: activeData.candles[currentIndex].ts,
      });
    },
  });

  const handleClosePosition = useCallback(async () => {
    if (!open || !currentCandle || !currentPrice) return;
    try {
      await closeTrade(open.id, {
        exit_ts: replayMode ? currentCandle.ts : (activeTick?.ts ?? currentCandle.ts),
        exit_price: currentPrice,
        result: "manual",
      });
      setOpen(null);
    } catch (e) { console.error(e); }
  }, [open, currentCandle, currentPrice, closeTrade, setOpen, replayMode, activeTick]);

  // Watchlist click → switch the ACTIVE pane's symbol
  const handleWatchlistSelect = useCallback(
    (entry: SymbolEntry) => {
      updatePane(activeId, { symbol: entry.symbol, exchange: entry.exchange, symbolName: entry.name });
    },
    [updatePane, activeId]
  );

  return (
    <Layout center={<ChartCountSelector />}>
      <div className="flex h-full">
        <DrawingToolbar
          symbol={active.symbol}
          exchange={active.exchange}
          timeframe={active.timeframe}
        />

        <div className="flex h-full min-w-0 flex-1 flex-col">
          {/* Top control bar — sub-toolbar above the grid */}
          <div className="flex shrink-0 items-center justify-between border-b border-border bg-bg-panel px-3 py-1.5">
            <div className="flex items-center gap-3">
              <IndicatorsButton />
              {singleMode ? (
                <button
                  onClick={() => (replayActive ? exitReplay() : enterReplay())}
                  className={clsx(
                    "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                    replayActive
                      ? "bg-accent text-white hover:bg-accent-hover"
                      : "border border-border bg-bg-elevated text-text-secondary hover:border-border-strong hover:text-text-primary"
                  )}
                  title="Toggle bar replay (R)"
                >
                  {replayActive ? (
                    <><X className="h-3.5 w-3.5" /><span>Exit Replay</span></>
                  ) : (
                    <>
                      <Scissors className="h-3.5 w-3.5" />
                      <span>Bar Replay</span>
                      <kbd className="rounded bg-bg-hover px-1 py-px text-2xs text-text-muted">R</kbd>
                    </>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-1.5 text-2xs text-text-muted" title="Switch to 1-chart view to use replay">
                  <Info className="h-3 w-3" />
                  <span>Replay available in single-chart view</span>
                </div>
              )}
              {activeTool && (
                <div className="flex items-center gap-1.5 rounded-md bg-accent/15 px-2 py-1 text-2xs text-accent">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
                  <span className="uppercase tracking-wider font-semibold">
                    {PROMPT_LABELS_BY_TOOL[activeTool]?.(pendingPoints.length) ?? activeTool}
                  </span>
                  <kbd className="ml-1 rounded bg-bg-hover px-1 py-px text-text-muted">Esc</kbd>
                </div>
              )}
            </div>
            <div className="text-2xs text-text-muted tabular">
              {!singleMode && <span>Active: {active.symbol} · {active.exchange}</span>}
            </div>
          </div>

          {/* Grid */}
          <div className={clsx("grid min-h-0 flex-1 gap-px bg-border", GRID_CLASS_BY_COUNT[count])}>
            {panes.map((p) => (
              <ChartPane
                key={p.id}
                pane={p}
                isActive={p.id === activeId}
                replayActiveForThisPane={singleMode && p.id === activeId}
                showCandleStats={count <= 2}
                compact={count >= 4}
                onMakeActive={() => setActive(p.id)}
              />
            ))}
          </div>

          {/* Replay bar + trade history — only in single mode */}
          {singleMode && (
            <>
              <ReplayBar
                candles={activeData?.candles ?? []}
                symbol={active.symbol}
                exchange={active.exchange}
                timeframe={active.timeframe}
              />
              <TradeHistory trades={trades} onDelete={(id) => deleteTrade(id)} />
            </>
          )}
        </div>

        <RightSidebar
          currentSymbol={active.symbol}
          currentExchange={active.exchange}
          onSelectSymbol={handleWatchlistSelect}
          currentPrice={currentPrice}
          autoCloseActive={replayMode}
          onBuy={() => placeOrder("long")}
          onSell={() => placeOrder("short")}
          onClosePosition={handleClosePosition}
          onUpdateSL={updateOpenSL}
          onUpdateTP={updateOpenTP}
          trades={trades}
        />
      </div>
    </Layout>
  );
}
